import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActionButtonVisibilityService } from '@netfoundry-ui/feature/action-button';
import { ScimIntegration } from '@netfoundry-ui/shared/model';
import { FeatureService, URLS } from '@netfoundry-ui/shared/services';
import { forgetFixedColumnWidths, ROW_MENU_ICONS } from '@netfoundry-ui/ui/table';
import _ from 'lodash';
import { skip } from 'rxjs/operators';
import { DataTableFilterService, FilterObj, ListColumn, ListSortDir, ListTableService } from 'ziti-console-lib';
import { ScimComponent } from './scim.component';

const TABLE_ID = 'scim-integrations-list';
const NAME_FILTER = 'name';
const URL_FILTER = 'integrationKey';
const DEFAULT_PAGE_SIZE = 30;

type ScimRow = ScimIntegration & { actionList?: string[]; hideSelect?: boolean; selected?: boolean };

@Component({
    selector: 'lib-scim-list',
    templateUrl: './scim-list.component.html',
    standalone: false,
})
export class ScimListComponent extends ScimComponent implements OnInit, OnDestroy {
    override tableId = TABLE_ID;
    readonly searchFilter = NAME_FILTER;
    readonly initialPageSize = DEFAULT_PAGE_SIZE;
    readonly tableOptions = { pager: 'pages' as const };
    readonly menuItems = [
        { label: 'Edit', action: 'update', icon: ROW_MENU_ICONS.edit },
        { label: 'Delete', action: 'delete', icon: ROW_MENU_ICONS.delete },
    ];
    readonly headerActions = [{ label: 'Download Selected', action: 'downloadCsv', icon: ROW_MENU_ICONS.download }];
    readonly tabs = [{ label: 'SCIM', url: URLS.SCIM_LIST, hidden: false }];

    columns: ListColumn<ScimRow>[] = [];
    filterApplied = false;

    private readonly tableFilters = inject(DataTableFilterService);
    private readonly createVisibility = inject(ActionButtonVisibilityService);
    private readonly features = inject(FeatureService);

    get rows(): ScimRow[] {
        const protectedDelete = this.features.enableDeleteProtection;
        (this.displayedItems as ScimRow[])?.forEach((row) => {
            row.hideSelect = protectedDelete || !row.actionList?.includes('delete');
        });
        return this.displayedItems ?? [];
    }

    get hasSelection(): boolean {
        return !!this.displayedItems?.some((row: ScimRow) => row.selected);
    }

    get canDeleteSelection(): boolean {
        return !this.createVisibility.isCancelledAccount;
    }

    // Same rule the old plus/minus button hid itself by, so Create follows it.
    get createBlocked(): boolean {
        return !this.canCreate || this.createVisibility.isCancelledAccount;
    }

    get hasPrev(): boolean {
        return this.svc.page > 1;
    }

    get hasNext(): boolean {
        return this.svc.page * this.svc.pageSize < this.svc.lastTotalCount;
    }

    get startCount(): number {
        return this.displayedItems?.length ? (this.svc.page - 1) * this.svc.pageSize + 1 : 0;
    }

    get endCount(): number {
        return this.displayedItems?.length ? (this.svc.page - 1) * this.svc.pageSize + this.displayedItems.length : 0;
    }

    override ngOnInit(): void {
        this.columns = this.buildColumns();
        forgetFixedColumnWidths(TABLE_ID, this.columns);
        this.svc.pageSize = ListTableService.readPersistedPageSize(TABLE_ID, DEFAULT_PAGE_SIZE);
        this.tableFilters.pageSize = this.svc.pageSize;
        this.tableFilters.clearFilters();
        this.svc.init();
        this.canCreate = this.svc.canCreate();

        this.subscription.add(
            this.svc.filteredRecords.subscribe((result) => {
                this.displayedItems = result || [];
            })
        );
        this.subscription.add(this.svc.isLoading.subscribe((loading) => (this.isLoading = loading)));
        this.subscription.add(
            this.svc.confirmDelete.subscribe((item: ScimIntegration) => this.openConfirm('delete', item))
        );
        this.subscription.add(
            this.tableFilters.filtersChanged.subscribe((filters) => {
                this.applyFilters(filters);
                this.restart();
            })
        );
        this.subscription.add(
            this.tableFilters.pageChanged.pipe(skip(1)).subscribe((page) => {
                this.svc.page = Math.max(1, Number(page) || 1);
                this.reload();
            })
        );
        this.subscription.add(
            this.tableFilters.pageSizeChanged.pipe(skip(1)).subscribe((size) => {
                this.svc.pageSize = size;
                this.restart();
            })
        );
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.tableFilters.clearFilters();
        this.svc.columnFilters = { name: '' };
        this.svc.filterString = '';
        this.svc.pageSize = DEFAULT_PAGE_SIZE;
    }

    onHeaderAction(action: string): void {
        if (action === 'add') {
            this.edit(true);
        } else if (action === 'delete' && this.hasSelection) {
            this.openConfirm('delete');
        }
    }

    onAction(event: { action: string; item?: ScimRow }): void {
        const row = event.item;
        switch (event.action) {
            case 'create':
                this.edit(true);
                break;
            case 'update':
                if (row) this.edit(true, row);
                break;
            case 'delete':
                if (row) {
                    this.displayedItems.forEach((item: ScimRow) => (item.selected = false));
                    this.svc.confirmAction('delete', row);
                }
                break;
            case 'downloadCsv':
                this.downloadCsv(undefined);
                break;
        }
    }

    private applyFilters(filters: FilterObj[]): void {
        const next: Record<string, string> = { [NAME_FILTER]: '', [URL_FILTER]: '' };
        filters.forEach((item) => {
            if (item.columnId in next) {
                next[item.columnId] = String(item.value ?? '').trim();
            }
        });
        this.svc.columnFilters = next;
        this.svc.filterString = next[NAME_FILTER];
        this.filterApplied = Object.values(next).some((value) => !!value);
    }

    private restart(): void {
        this.svc.page = 1;
        this.tableFilters.currentPage = 1;
        this.reload();
    }

    private reload(): void {
        this.svc.clearSelected();
        this.svc.debouncedRefreshRecords();
    }

    private buildColumns(): ListColumn<ScimRow>[] {
        const open = (ctx: { row: ScimRow }) => this.edit(true, ctx.row);
        const onSort = (key: string, dir: ListSortDir) => this.svc.sort(key, dir === 'desc' ? 'DESC' : 'ASC');
        return [
            {
                key: NAME_FILTER,
                label: 'Name',
                minWidth: 200,
                sortable: true,
                sortDir: 'asc',
                filterType: 'TEXTINPUT',
                onSort,
                valueFormatter: (shim) => shim.data.name || shim.data.id || '',
                onCellClick: open,
            },
            {
                key: 'hasAuthToken',
                label: 'Has Auth Token',
                width: 150,
                valueFormatter: (shim) => (shim.data.serviceIdentityId && shim.data.jwtConnectionId ? 'Yes' : 'No'),
                onCellClick: open,
            },
            {
                key: URL_FILTER,
                label: 'Integration URL',
                minWidth: 300,
                sortable: true,
                filterType: 'TEXTINPUT',
                onSort,
                valueFormatter: (shim) => _.escape(this.getIntegrationUrl(shim.data)),
                onCellClick: (ctx) => this.copy(ctx.row),
            },
            {
                key: 'id',
                label: 'Id',
                minWidth: 200,
                hidden: true,
                valueFormatter: (shim) => shim.data.id || '',
                onCellClick: open,
            },
        ];
    }
}
